import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { User, Flame, Star, Layers, ChevronRight, ChevronLeft, Trophy, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";

function Thumb({ grid }) {
  const cols = grid?.[0]?.length || 12;
  return (
    <div
      className="aspect-[3/2] border border-white/10 grid gap-[1px] p-1 bg-black/40"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
    >
      {(grid || []).map((row, r) =>
        row.map((v, c) => {
          const bg =
            v === 1 ? "#00ffff" :
            v === 2 ? "#ffd700" :
            v === 3 ? "#ff007f" :
            v === 4 ? "#b026ff" : "transparent";
          return <div key={`${r}-${c}`} style={{ background: bg }} />;
        })
      )}
    </div>
  );
}

export default function Creator() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    api.get(`/community/creator/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, [id]);

  // Fetch the achievement catalog once so we can map unlocked ids → names/tiers.
  useEffect(() => {
    api.get("/achievements")
      .then((r) => setCatalog(r.data.catalog || []))
      .catch(() => setCatalog([]));
  }, []);

  if (data === null) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" /> <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          LOADING CREATOR...
        </main>
      </div>
    );
  }
  if (data === false) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" /> <Navbar />
        <main className="relative z-10 max-w-2xl mx-auto pt-28 px-6 text-center">
          <div className="font-mono-cosmic text-[11px] tracking-[0.3em] text-[#ff007f] uppercase mb-2">
            // 404
          </div>
          <h1 className="font-heading font-black uppercase text-3xl tracking-tighter">Creator not found</h1>
          <Link to="/community" className="btn-neon mt-6 inline-block" data-testid="creator-404-back">
            Back to Community
          </Link>
        </main>
      </div>
    );
  }

  const { author_name, items, stats, achievements = [], karma = null, loadout = null } = data;
  const unlockedDetails = achievements
    .map((aid) => catalog.find((c) => c.id === aid))
    .filter(Boolean);
  const TIER_COLORS = {
    bronze: "#cd7f32",
    silver: "#c0c0c0",
    gold: "#ffd700",
    legendary: "#b026ff",
  };

  // Toxicity karma badge tier → display config. `gold/silver/clean`
  // get a positive rim treatment; `warned` gets a dim red marker;
  // `neutral` is suppressed entirely (no signal = no UI).
  const KARMA_BADGE = karma && karma.tier && {
    gold: { label: "GOLD CONDUCT", color: "#ffd700", glow: "rgba(255,215,0,0.4)" },
    silver: { label: "SILVER CONDUCT", color: "#c0c0c0", glow: "rgba(192,192,192,0.35)" },
    clean: { label: "CLEAN RECORD", color: "#7cffb0", glow: "rgba(124,255,176,0.3)" },
    warned: { label: "REPORTED", color: "#ff4d6d", glow: "rgba(255,77,109,0.25)" },
  }[karma.tier];

  return (
    <div className="relative min-h-screen">
      <div className="starfield" /> <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-4 pt-24 pb-16 fade-in">
        <Link
          to="/community"
          className="inline-flex items-center gap-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 hover:text-white uppercase mb-4"
          data-testid="creator-back"
        >
          <ChevronLeft className="w-3 h-3" /> Community
        </Link>

        <header className="glass p-6 border border-white/15 mb-6" data-testid="creator-header">
          <div className="flex items-start gap-5 flex-wrap">
            <div
              className="w-16 h-16 border border-[#b026ff] text-[#b026ff] flex items-center justify-center flex-shrink-0"
              style={{ boxShadow: "0 0 18px rgba(176,38,255,0.3)" }}
            >
              <User className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#b026ff] uppercase mb-1">
                // PILOT PROFILE
              </div>
              <h1 className="font-heading font-black uppercase text-2xl sm:text-3xl md:text-4xl tracking-tighter truncate">
                {author_name}
              </h1>
              {KARMA_BADGE && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <div
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
                    style={{
                      borderColor: KARMA_BADGE.color,
                      color: KARMA_BADGE.color,
                      boxShadow: `0 0 8px ${KARMA_BADGE.glow}`,
                    }}
                    title={
                      karma.tier === "warned"
                        ? `${karma.reports_against} report${karma.reports_against === 1 ? "" : "s"} from the community`
                        : `${karma.brackets_played} bracket${karma.brackets_played === 1 ? "" : "s"} · 0 reports`
                    }
                    data-testid={`creator-karma-${karma.tier}`}
                  >
                    <span>{KARMA_BADGE.label}</span>
                    {karma.tier !== "warned" && (
                      <span className="opacity-65">· {karma.brackets_played} BRKT</span>
                    )}
                  </div>
                  {karma.pilot_score > 0 && (
                    <div
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#3aa8ff]/55 text-[#3aa8ff] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
                      title={`Score = brackets×10 + wins×50 + rivalries×5 - reports×100. Wins: ${karma.bracket_wins}, rivals: ${karma.rivalry_depth}.`}
                      data-testid="creator-pilot-score"
                    >
                      <span>PILOT SCORE</span>
                      <span className="text-white tabular-nums">
                        {karma.pilot_score.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {/* Suppress badge UI but still show pilot score for
                  neutral-tier pilots who have a non-zero score. */}
              {!KARMA_BADGE && karma && karma.pilot_score > 0 && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 border border-[#3aa8ff]/55 text-[#3aa8ff] font-mono-cosmic text-[9px] tracking-[0.25em] uppercase"
                  title={`Score = brackets×10 + wins×50 + rivalries×5 - reports×100. Wins: ${karma.bracket_wins}, rivals: ${karma.rivalry_depth}.`}
                  data-testid="creator-pilot-score"
                >
                  <span>PILOT SCORE</span>
                  <span className="text-white tabular-nums">
                    {karma.pilot_score.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 w-full sm:w-auto sm:min-w-[260px]">
              <Stat icon={<Layers className="w-3.5 h-3.5" />} label="Levels" value={stats.levels} color="#00ffff" />
              <Stat icon={<Flame  className="w-3.5 h-3.5" />} label="Plays"  value={stats.total_plays} color="#ff8c1a" />
              <Stat
                icon={<Star className="w-3.5 h-3.5" />}
                label="Avg Stars"
                value={stats.total_ratings > 0 ? stats.avg_stars.toFixed(1) : "—"}
                color="#ffd700"
              />
            </div>
          </div>
        </header>

        {unlockedDetails.length > 0 && (
          <section className="mb-6" data-testid="creator-achievements-strip">
            <div className="flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.35em] text-[#ffd700] uppercase mb-2">
              <Trophy className="w-3.5 h-3.5" />
              Unlocked Trophies · {unlockedDetails.length}
            </div>
            <div className="glass p-3 border border-white/10 flex items-center gap-2 overflow-x-auto">
              {unlockedDetails.map((a) => {
                const color = TIER_COLORS[a.tier] || "#ffffff";
                return (
                  <div
                    key={a.id}
                    className="flex-shrink-0 w-36 px-2 py-2 border flex items-center gap-2"
                    style={{
                      borderColor: `${color}55`,
                      background: `${color}0f`,
                      boxShadow: `inset 0 0 20px ${color}18`,
                    }}
                    title={a.desc}
                    data-testid={`creator-ach-${a.id}`}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center flex-shrink-0"
                      style={{ color, border: `1px solid ${color}` }}
                    >
                      <Trophy className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div
                        className="font-heading font-bold uppercase text-[11px] tracking-tight truncate"
                        style={{ color }}
                      >
                        {a.name}
                      </div>
                      <div className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-white/40">
                        {a.tier}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {loadout && (loadout.paddle || loadout.ball) && (
          <section className="mb-6" data-testid="creator-loadout-strip">
            <div className="flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              Artifact Loadout
            </div>
            <div className="glass p-3 border border-[#b026ff]/25 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {["paddle", "ball"].map((slot) => {
                const art = loadout[slot];
                if (!art) {
                  return (
                    <div
                      key={slot}
                      className="px-3 py-2.5 border border-dashed border-white/12 flex items-center gap-3"
                      data-testid={`creator-loadout-${slot}-empty`}
                    >
                      <div className="w-9 h-9 flex items-center justify-center text-base text-white/25 border border-white/8">
                        —
                      </div>
                      <div className="min-w-0">
                        <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/35">
                          {slot}
                        </div>
                        <div className="font-mono-cosmic text-[10px] text-white/35">
                          No artifact equipped
                        </div>
                      </div>
                    </div>
                  );
                }
                const accent = slot === "paddle" ? "#b026ff" : "#ffd700";
                const isMaxed = art.is_maxed;
                return (
                  <div
                    key={slot}
                    className="px-3 py-2.5 border flex items-center gap-3"
                    style={{
                      borderColor: `${accent}55`,
                      background: `${accent}0d`,
                      boxShadow: isMaxed ? `inset 0 0 22px ${accent}33` : undefined,
                    }}
                    title={`${art.name} · L${art.level} of ${art.max_level} · ${art.tier_label}`}
                    data-testid={`creator-loadout-${slot}`}
                  >
                    <div
                      className="w-9 h-9 flex items-center justify-center text-base flex-shrink-0 border"
                      style={{ borderColor: `${accent}` }}
                    >
                      {art.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
                        {slot}
                        {isMaxed && (
                          <span
                            className="ml-1.5 px-1 border text-[8px] tracking-[0.2em]"
                            style={{
                              borderColor: accent,
                              color: accent,
                              boxShadow: `0 0 6px ${accent}aa`,
                            }}
                          >
                            MAX
                          </span>
                        )}
                      </div>
                      <div
                        className="font-heading uppercase text-sm tracking-tight truncate"
                        style={{ color: accent }}
                      >
                        {art.name}
                      </div>
                      <div className="font-mono-cosmic text-[10px] text-white/55 truncate tabular-nums">
                        L{art.level} · {art.tier_label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {items.length === 0 ? (
          <div className="border border-dashed border-white/15 px-6 py-20 text-center font-mono-cosmic text-[11px] tracking-[0.3em] text-white/40 uppercase">
            // NO LEVELS YET
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creator-levels-grid">
            {items.map((lvl) => (
              <Link
                key={lvl.code}
                to={`/community/${lvl.code}`}
                className="glass p-4 border border-white/10 hover:border-[#00ffff]/60 transition block"
                data-testid={`creator-card-${lvl.code}`}
              >
                <Thumb grid={lvl.grid} />
                <div className="mt-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading uppercase text-sm tracking-tight text-white truncate">
                      {lvl.name}
                    </div>
                    <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50">
                      {lvl.code}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </div>
                <div className="mt-2 flex items-center gap-3 font-mono-cosmic text-[10px] tracking-[0.2em] uppercase">
                  <span className="inline-flex items-center gap-1 text-[#ff8c1a]">
                    <Flame className="w-3 h-3" /> {lvl.plays}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#ffd700]">
                    <Star className="w-3 h-3" />
                    {lvl.stars_count > 0 ? `${lvl.stars_avg.toFixed(1)} (${lvl.stars_count})` : "—"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ icon, label, value, color }) {
  return (
    <div className="glass px-3 py-2 border" style={{ borderColor: `${color}66` }}>
      <div className="flex items-center gap-1.5 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/50">
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <div className="font-heading font-black text-lg" style={{ color }}>{value}</div>
    </div>
  );
}
